import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { BASE_URL } from "../../utils/constants";

const HotelAmenityMappingForm = ({
  initialData,
  handleClose,
  onSubmit,
  hotels,
}) => {
  const [amenities, setAmenities] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filteredAmenities, setFilteredAmenities] = useState([]);
  const [filteredRoomTypes, setFilteredRoomTypes] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);

  const safeInitialData = initialData || {};

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id: safeInitialData.id || null,
      hotel_id: safeInitialData.hotel_id || "",
      amenitie_id: "",
      room_type: "",
      activity_type: "",
      status: safeInitialData.status || "1",
    },
  });

  const hotel_id = watch("hotel_id");

  useEffect(() => {
    axios.get(`${BASE_URL}HotelAmenity`).then((res) => {
      if (res.data.success) setAmenities(res.data.data || []);
    });

    axios.get(`${BASE_URL}HotelRoom`).then((res) => {
      if (res.data.success) setRoomTypes(res.data.data || []);
    });

    axios.get(`${BASE_URL}HmActivity`).then((res) => {
      if (res.data.success) setActivities(res.data.data || []);
    });
  }, []);

  useEffect(() => {
    if (hotel_id) {
      const hId = parseInt(hotel_id);
      const fa = amenities.filter((a) => a.hotel_id === hId);
      const fr = roomTypes.filter((r) => r.hotel_id === hId);
      const fac = activities.filter((a) => a.hotel_id === hId);

      setFilteredAmenities(fa);
      setFilteredRoomTypes(fr);
      setFilteredActivities(fac);

      // Set matching initial values if editing
      if (initialData?.amenitie_id) {
        const matchedAmenity = fa.find(
          (a) => a.am_unique_id === parseInt(initialData.amenitie_id),
        );
        if (matchedAmenity)
          setValue("amenitie_id", matchedAmenity.am_unique_id.toString());
      }

      if (initialData?.room_type) {
        const matchedRoom = fr.find(
          (r) => r.room_type === initialData.room_type,
        );
        if (matchedRoom) setValue("room_type", matchedRoom.room_type);
      }

      if (initialData?.activity_type) {
        const matchedActivity = fac.find(
          (a) =>
            a.activity_name === initialData.activity_type ||
            a.activity_id === parseInt(initialData.activity_type),
        );
        if (matchedActivity)
          setValue("activity_type", matchedActivity.activity_name);
      }
    } else {
      setFilteredAmenities([]);
      setFilteredRoomTypes([]);
      setFilteredActivities([]);
    }
  }, [hotel_id, amenities, roomTypes, activities, setValue, initialData]);

  const submitForm = async (formData) => {
    const payload = {
      ...formData,
      hotel_id: parseInt(formData.hotel_id),
      amenitie_id: parseInt(formData.amenitie_id),
      activity_type: formData.activity_type, // activity_name
    };
    onSubmit(payload);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(submitForm)} sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Hotel Amenity Mapping
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="hotel_id"
            control={control}
            rules={{ required: "Hotel is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Hotel"
                size="small"
                error={!!errors.hotel_id}
                helperText={errors.hotel_id?.message}
              >
                <MenuItem value="">Select Hotel</MenuItem>
                {hotels
                  .filter((hotel) => hotel.is_active === true)
                  .map((h) => (
                    <MenuItem key={h.hotel_id} value={h.hotel_id}>
                      {h.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="amenitie_id"
            control={control}
            rules={{ required: "Amenity is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Amenity"
                size="small"
                error={!!errors.amenitie_id}
                helperText={errors.amenitie_id?.message}
              >
                <MenuItem value="">Select Amenity</MenuItem>
                {filteredAmenities.map((a) => (
                  <MenuItem key={a.am_unique_id} value={a.am_unique_id}>
                    {a.am_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="room_type"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Room Type"
                size="small"
                error={!!errors.room_type}
                helperText={errors.room_type?.message}
              >
                <MenuItem value="">Select Room Type</MenuItem>
                {filteredRoomTypes.map((r) => (
                  <MenuItem key={r.room_id} value={r.room_type}>
                    {r.room_type}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="activity_type"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Activity"
                size="small"
                error={!!errors.activity_type}
                helperText={errors.activity_type?.message}
              >
                <MenuItem value="">Select Activity</MenuItem>
                {filteredActivities.map((a) => (
                  <MenuItem key={a.activity_id} value={a.activity_name}>
                    {a.activity_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="status"
            control={control}
            rules={{ required: "Status is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Status"
                size="small"
                error={!!errors.status}
                helperText={errors.status?.message}
              >
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </TextField>
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {safeInitialData?.id ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default HotelAmenityMappingForm;
